import { OPERATION_IDS, ROUTE_IDS } from '../routes/ids.js';
import { ORGANIZATION_USERS_UNLOCK, ORGANIZATION_USERS_FORCE_PASSWORD_RESET, ORGANIZATION_USERS_MFA_REQUIRE_ENROLLMENT, ORGANIZATION_USERS_MFA_RESET_ENROLLMENT, ORGANIZATION_USERS_MFA_DISABLE, } from '../../operations/operation-ids.js';
import { getRouteById } from '../routes/effective-routes.js';
import { MAINTENANCE_DELEGATION_TABLE } from './effective-support-delegation-table.js';
import { MARKET_DATA_TABLE_REGISTRY, MARKET_PRICES_TABLE, } from './market-data-table-registry.js';
import { PORTFOLIO_PERFORMANCE_TABLE } from './portfolio-performance-table-registry.js';
import { PLATFORM_ACCESS_INSPECTOR_TABLE, PLATFORM_ADMIN_TABLE_REGISTRY, PLATFORM_AUDIT_TABLE, PLATFORM_USERS_TABLE, } from './platform-admin-live-descriptors.js';
import { PLATFORM_MODULES_TABLE } from './effective-platform-modules-table.js';
import { PLATFORM_ORGANIZATIONS_TABLE } from './platform-organization-table.js';
import { ORGANIZATION_TABLE, USERS_TABLE as BASE_USERS_TABLE, ROLES_TABLE as BASE_ROLES_TABLE, AUDIT_TABLE, INSTRUMENT_TABLE, CORPORATE_ACTION_TABLE, FACT_BATCH_TABLE, IMPORT_JOB_TABLE as BASE_IMPORT_JOB_TABLE, OPENING_CUTOVER_TABLE as BASE_OPENING_CUTOVER_TABLE, RECONCILIATION_RUN_TABLE, EXPERIENCE_TABLE_REGISTRY as BASE_TABLE_REGISTRY, } from './table-registry.js';
const IMPORT_TYPES = [
    'INSTRUMENT',
    'TRANSACTION',
    'MARKET_DATA',
    'CORPORATE_ACTION',
    'CUSTODY_POSITION',
    'OPENING_CUTOVER',
    'TAX_RULE',
];
function routePath(routeId, rowField = 'id') {
    const route = getRouteById(routeId);
    if (!route)
        throw new Error(`Unknown canonical action route: ${String(routeId)}`);
    return route.path
        .replace(/\{[A-Za-z0-9_-]+\}/, `{${rowField}}`)
        .replace(/:[A-Za-z0-9_-]+/, `:${rowField}`);
}
function editAction(id, table, operationId, routeId, order) {
    return {
        id,
        schemaVersion: table.schemaVersion,
        module: table.module,
        feature: table.feature,
        privacyClassification: table.privacyClassification,
        telemetryId: `${table.telemetryId}.${id}`,
        accessibilityLabelKey: 'action.edit',
        availability: 'AVAILABLE',
        owner: 'experience-registry',
        operationId: operationId,
        labelKey: 'action.edit',
        iconKey: 'edit',
        kind: 'navigate',
        intent: 'primary',
        idempotencyRequired: false,
        successBehavior: 'NAVIGATE_TO',
        targetRouteId: routeId,
        successNavigateTo: routePath(routeId),
        errorBehavior: 'SHOW_INLINE_ERROR',
        order,
        group: 'navigation',
    };
}
function deleteAction(id, table, operationId, commandId, confirmationTitleKey, confirmationMessageKey, successMessageKey, order) {
    return {
        id,
        schemaVersion: table.schemaVersion,
        module: table.module,
        feature: table.feature,
        privacyClassification: table.privacyClassification,
        telemetryId: `${table.telemetryId}.${id}`,
        accessibilityLabelKey: 'action.delete',
        availability: 'AVAILABLE',
        owner: 'experience-registry',
        operationId: operationId,
        labelKey: 'action.delete',
        iconKey: 'delete',
        kind: 'command',
        method: 'DELETE',
        commandId: commandId,
        intent: 'danger',
        idempotencyRequired: true,
        confirmation: {
            titleKey: confirmationTitleKey,
            messageKey: confirmationMessageKey,
            confirmLabelKey: 'action.delete',
            cancelLabelKey: 'action.cancel',
        },
        successBehavior: 'REFRESH_TABLE',
        successMessageKey: successMessageKey,
        errorBehavior: 'SHOW_INLINE_ERROR',
        errorMessageKey: 'common.error.unexpected',
        order,
        group: 'danger',
    };
}
/**
 * Granular user lifecycle row action (unlock / force password reset / MFA
 * admin). Mirrors the shape of `deleteAction` but is POST-mutating, carries an
 * arbitrary intent, and optionally collects a free-text `reason` field with
 * the confirmation dialog (the MFA admin service requires a reason for
 * audit-grade attribution).
 */
function userCommandAction(id, table, operationId, commandId, labelKey, iconKey, intent, successMessageKey, order, options) {
    return {
        id,
        schemaVersion: table.schemaVersion,
        module: table.module,
        feature: table.feature,
        privacyClassification: table.privacyClassification,
        telemetryId: `${table.telemetryId}.${id}`,
        accessibilityLabelKey: labelKey,
        availability: 'AVAILABLE',
        owner: 'experience-registry',
        operationId: operationId,
        labelKey: labelKey,
        iconKey: iconKey,
        kind: 'command',
        method: 'POST',
        commandId: commandId,
        intent,
        idempotencyRequired: true,
        confirmation: {
            titleKey: options.confirmationTitleKey,
            messageKey: options.confirmationMessageKey,
            confirmLabelKey: options.confirmLabelKey,
            cancelLabelKey: 'action.cancel',
            ...(options.requireReason
                ? {
                    fields: [
                        {
                            name: 'reason',
                            labelKey: 'user:action.reasonLabel',
                            fieldType: 'text',
                            required: true,
                        },
                    ],
                }
                : {}),
        },
        successBehavior: 'REFRESH_TABLE',
        successMessageKey: successMessageKey,
        errorBehavior: 'SHOW_INLINE_ERROR',
        errorMessageKey: 'common.error.unexpected',
        visibilityCondition: options.visibilityCondition,
        order,
        group: intent === 'danger' ? 'danger' : 'secondary',
    };
}
function userColumns(columns) {
    return columns.map((column) => {
        const filterable = column.field === 'branchId' || column.field === 'membershipStatus';
        return {
            ...column,
            sortable: column.field === 'displayName' || column.field === 'email',
            filterable,
            allowedFilterOperators: filterable ? ['equals'] : undefined,
        };
    });
}
function roleColumns(columns) {
    return columns.map((column) => {
        const filterable = column.field === 'status';
        return {
            ...column,
            sortable: false,
            filterable,
            allowedFilterOperators: filterable ? ['equals'] : undefined,
        };
    });
}
function importColumns(columns) {
    return columns.map((column) => column.field === 'importType'
        ? {
            ...column,
            enumOptions: IMPORT_TYPES.map((value) => ({
                value,
                labelKey: `enum.importType.${value}`,
            })),
        }
        : column);
}
export const USERS_TABLE = {
    ...BASE_USERS_TABLE,
    columns: userColumns(BASE_USERS_TABLE.columns),
    defaultSort: BASE_USERS_TABLE.defaultSort,
    serverSideSorting: true,
    capabilities: {
        ...(BASE_USERS_TABLE.capabilities ?? {}),
        sorting: true,
        filtering: true,
        globalSearch: true,
    },
    rowActions: [
        editAction('action.organizationUser.edit', BASE_USERS_TABLE, OPERATION_IDS.ORGANIZATION_USERS_UPDATE, ROUTE_IDS.ORGANIZATION_USERS_EDIT, 10),
        deleteAction('action.organizationUser.delete', BASE_USERS_TABLE, OPERATION_IDS.ORGANIZATION_USERS_DISABLE, 'cmd.admin.organization-users.delete', 'user:confirmDeleteTitle', 'user:confirmDelete', 'user:deleteSuccess', 20),
        // Granular credential/MFA lifecycle. These reuse the existing
        // organization-scoped operations, permissions, and service methods; only
        // the row-action wiring is new. AAL2 is enforced by the operation guard.
        userCommandAction('action.organizationUser.unlock', BASE_USERS_TABLE, ORGANIZATION_USERS_UNLOCK, 'cmd.admin.organization-users.unlock', 'user:action.unlock', 'lock', 'primary', 'user:unlockSuccess', 50, {
            confirmationTitleKey: 'user:confirmUnlockTitle',
            confirmationMessageKey: 'user:confirmUnlock',
            confirmLabelKey: 'user:action.unlock',
            // P0-3: Lock status + self-modification eligibility is server-authoritative
            // via row.actionEligibility. Permission is checked by the backend resolver.
            visibilityCondition: {
                operator: 'permissionAvailable',
                permission: 'organization:users:unlock',
            },
        }),
        userCommandAction('action.organizationUser.forcePasswordReset', BASE_USERS_TABLE, ORGANIZATION_USERS_FORCE_PASSWORD_RESET, 'cmd.admin.organization-users.force-password-reset', 'user:action.forcePasswordReset', 'key', 'warning', 'user:forcePasswordResetSuccess', 60, {
            confirmationTitleKey: 'user:confirmForcePasswordResetTitle',
            confirmationMessageKey: 'user:confirmForcePasswordReset',
            confirmLabelKey: 'user:action.forcePasswordReset',
            // P0-3: Self-modification eligibility is server-authoritative via
            // row.actionEligibility. Permission is checked by the backend resolver.
            visibilityCondition: {
                operator: 'permissionAvailable',
                permission: 'organization:users:force-password-reset',
            },
        }),
        userCommandAction('action.organizationUser.mfaRequireEnrollment', BASE_USERS_TABLE, ORGANIZATION_USERS_MFA_REQUIRE_ENROLLMENT, 'cmd.admin.organization-users.mfa-require-enrollment', 'user:action.mfaRequireEnrollment', 'security', 'primary', 'user:mfaRequireEnrollmentSuccess', 70, {
            confirmationTitleKey: 'user:confirmMfaRequireEnrollmentTitle',
            confirmationMessageKey: 'user:confirmMfaRequireEnrollment',
            confirmLabelKey: 'user:action.mfaRequireEnrollment',
            requireReason: true,
            // P0-3: MFA state + self-modification eligibility is server-authoritative
            // via row.actionEligibility. Permission is checked by the backend resolver.
            visibilityCondition: {
                operator: 'permissionAvailable',
                permission: 'organization:users:mfa:require-enrollment',
            },
        }),
        userCommandAction('action.organizationUser.mfaResetEnrollment', BASE_USERS_TABLE, ORGANIZATION_USERS_MFA_RESET_ENROLLMENT, 'cmd.admin.organization-users.mfa-reset-enrollment', 'user:action.mfaResetEnrollment', 'refresh', 'warning', 'user:mfaResetEnrollmentSuccess', 80, {
            confirmationTitleKey: 'user:confirmMfaResetEnrollmentTitle',
            confirmationMessageKey: 'user:confirmMfaResetEnrollment',
            confirmLabelKey: 'user:action.mfaResetEnrollment',
            requireReason: true,
            // P0-3: MFA state + self-modification eligibility is server-authoritative
            // via row.actionEligibility. Permission is checked by the backend resolver.
            visibilityCondition: {
                operator: 'permissionAvailable',
                permission: 'organization:users:mfa:reset-enrollment',
            },
        }),
        userCommandAction('action.organizationUser.mfaDisable', BASE_USERS_TABLE, ORGANIZATION_USERS_MFA_DISABLE, 'cmd.admin.organization-users.mfa-disable', 'user:action.mfaDisable', 'lock', 'danger', 'user:mfaDisableSuccess', 90, {
            confirmationTitleKey: 'user:confirmMfaDisableTitle',
            confirmationMessageKey: 'user:confirmMfaDisable',
            confirmLabelKey: 'user:action.mfaDisable',
            requireReason: true,
            // P0-3: MFA state + self-modification eligibility is server-authoritative
            // via row.actionEligibility. Permission is checked by the backend resolver.
            visibilityCondition: {
                operator: 'permissionAvailable',
                permission: 'organization:users:mfa:disable',
            },
        }),
    ],
};
export const ROLES_TABLE = {
    ...BASE_ROLES_TABLE,
    columns: roleColumns(BASE_ROLES_TABLE.columns),
    defaultSort: undefined,
    serverSideSorting: false,
    capabilities: {
        ...(BASE_ROLES_TABLE.capabilities ?? {}),
        sorting: false,
        filtering: true,
        globalSearch: true,
    },
    rowActions: [
        editAction('action.organizationRole.edit', BASE_ROLES_TABLE, OPERATION_IDS.ORGANIZATION_ROLES_UPDATE, ROUTE_IDS.ORGANIZATION_ROLES_EDIT, 10),
        deleteAction('action.organizationRole.delete', BASE_ROLES_TABLE, OPERATION_IDS.ORGANIZATION_ROLES_DISABLE, 'cmd.admin.organization-roles.delete', 'role:confirmDeleteTitle', 'role:confirmDelete', 'role:deleteSuccess', 20),
    ],
};
export const IMPORT_JOB_TABLE = {
    ...BASE_IMPORT_JOB_TABLE,
    columns: importColumns(BASE_IMPORT_JOB_TABLE.columns),
};
const approveOpeningCutoverAction = {
    id: 'action.openingCutover.approve',
    schemaVersion: BASE_OPENING_CUTOVER_TABLE.schemaVersion,
    module: BASE_OPENING_CUTOVER_TABLE.module,
    feature: BASE_OPENING_CUTOVER_TABLE.feature,
    privacyClassification: BASE_OPENING_CUTOVER_TABLE.privacyClassification,
    telemetryId: `${BASE_OPENING_CUTOVER_TABLE.telemetryId}.approve`,
    accessibilityLabelKey: 'openingCutover.actions.approve',
    availability: 'AVAILABLE',
    owner: 'accounting',
    operationId: OPERATION_IDS.ACCOUNTING_OPENING_CUTOVER_APPROVE,
    labelKey: 'action.approve',
    iconKey: 'approve',
    kind: 'command',
    commandId: 'cmd.admin.organization-opening-cutover.approve',
    method: 'POST',
    submitBehavior: 'standalone',
    intent: 'primary',
    idempotencyRequired: true,
    confirmation: {
        titleKey: 'openingCutover.approve.title',
        messageKey: 'openingCutover.approve.message',
        confirmLabelKey: 'action.approve',
        cancelLabelKey: 'action.cancel',
    },
    successBehavior: 'REFRESH_TABLE',
    successMessageKey: 'openingCutover.messages.approveSuccess',
    errorBehavior: 'SHOW_INLINE_ERROR',
    errorMessageKey: 'common.error.unexpected',
    // P0-2: Status + maker-checker eligibility is server-authoritative via
    // row.actionEligibility. Permission is checked by the backend resolver.
    order: 10,
    group: 'primary',
};
export const OPENING_CUTOVER_TABLE = {
    ...BASE_OPENING_CUTOVER_TABLE,
    columns: BASE_OPENING_CUTOVER_TABLE.columns.map((column) => column.field === 'asOfDate'
        ? { ...column, field: 'cutoverDate', labelKey: 'openingCutover.fields.cutoverDate' }
        : column),
    defaultSort: { field: 'cutoverDate', direction: 'desc' },
    rowActions: [approveOpeningCutoverAction],
};
export { ORGANIZATION_TABLE, AUDIT_TABLE, INSTRUMENT_TABLE, CORPORATE_ACTION_TABLE, FACT_BATCH_TABLE, RECONCILIATION_RUN_TABLE, MAINTENANCE_DELEGATION_TABLE, MARKET_PRICES_TABLE, PORTFOLIO_PERFORMANCE_TABLE, PLATFORM_ORGANIZATIONS_TABLE, PLATFORM_USERS_TABLE, PLATFORM_MODULES_TABLE, PLATFORM_AUDIT_TABLE, PLATFORM_ACCESS_INSPECTOR_TABLE, };
export const EXPERIENCE_TABLE_REGISTRY = Object.freeze({
    ...BASE_TABLE_REGISTRY,
    ...MARKET_DATA_TABLE_REGISTRY,
    ...PLATFORM_ADMIN_TABLE_REGISTRY,
    [String(PLATFORM_ORGANIZATIONS_TABLE.id)]: PLATFORM_ORGANIZATIONS_TABLE,
    [String(PLATFORM_MODULES_TABLE.id)]: PLATFORM_MODULES_TABLE,
    [PORTFOLIO_PERFORMANCE_TABLE.id]: PORTFOLIO_PERFORMANCE_TABLE,
    [USERS_TABLE.id]: USERS_TABLE,
    [ROLES_TABLE.id]: ROLES_TABLE,
    [IMPORT_JOB_TABLE.id]: IMPORT_JOB_TABLE,
    [OPENING_CUTOVER_TABLE.id]: OPENING_CUTOVER_TABLE,
    [MAINTENANCE_DELEGATION_TABLE.id]: MAINTENANCE_DELEGATION_TABLE,
});
export function getExperienceTableDescriptor(id) {
    return EXPERIENCE_TABLE_REGISTRY[id];
}
